import { cn } from "@/lib/utils";
import GoldText from "./GoldText";
import DonLinear from "@/assets/icons/don-filled-linear.svg?react";
import { ComponentProps } from "react";

interface ChartDotProps extends ComponentProps<"div"> {
  invitedUsersCount: number;
  isVisible: boolean;
}

const ChartDot = ({
  className,
  invitedUsersCount,
  isVisible,
  ...props
}: ChartDotProps) => {
  if (!isVisible) return null;

  return (
    <div className={cn("absolute z-10", className)} {...props}>
      <div className="bg-white px-2 py-0.5 text-xs font-bold absolute -top-[calc(100%+8px)] rounded-md text-black absolute-x">
        {invitedUsersCount}
      </div>
      <div className={cn("size-4 bg-white flex-center rounded-full")}>
        <div className="size-3 bg-orange rounded-full shrink-0" />
      </div>
    </div>
  );
};

interface LinearPathProps {
  pathData: string;
  color: string;
  height: number;
}

const LinearPath = ({ pathData, color, height }: LinearPathProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="100"
    height={height}
    viewBox={`0 0 100 ${height}`}
    fill="none"
  >
    <path d={pathData} stroke={color} strokeWidth="4" strokeLinecap="round" />
  </svg>
);

const ChartFill = ({
  pathData,
  color,
  width,
  height,
}: {
  pathData: string;
  color: string;
  width: number;
  height: number;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox={`0 0 ${width} ${height}`}
    fill="none"
  >
    <path opacity="0.12" d={pathData} fill={color} />
  </svg>
);

interface ChartItemProps {
  isActive: boolean;
  forceToCenter?: boolean;
  startTop: number;
  endTop: number;
  showDot: boolean;
  donAwardAmount: number;
  invitedUsersCount: number;
  invitationRange: string;
  dotPosition?: string;
  linearPathData: string;
  linearHeight: number;
  fillPathData: string;
  fillWidth: number;
  fillHeight: number;
  min: number;
  max: number;
}

function mapProgressToNumber(
  progress: number,
  start: number,
  end: number
): number {
  const clampedProgress = Math.min(1, Math.max(0, progress));
  return start + (end - start) * clampedProgress;
}

const ChartItem = ({
  isActive,
  invitedUsersCount,
  // dotPosition = "",
  linearPathData,
  linearHeight,
  fillPathData,
  fillWidth,
  fillHeight,
  startTop,
  endTop,
  invitationRange,
  showDot,
  donAwardAmount,
  max,
  min,
  forceToCenter,
}: ChartItemProps) => {
  const color = isActive ? "#EBA132" : "#A9A9AD";
  const range = max - min;
  let progress =
    invitedUsersCount >= max ? 1 : (invitedUsersCount - min) / range;
  progress = Math.min(1, Math.max(0, progress)); // Clamp between 0-1

  const leftPosition = `${progress * 100}%`;
  const topPosition = mapProgressToNumber(progress, startTop, endTop);

  return (
    <div>
      <div className="relative">
        <ChartDot
          invitedUsersCount={invitedUsersCount}
          className="left-1/2 -translate-x-1/2"
          isVisible={showDot}
          style={{
            left: forceToCenter ? "50%" : leftPosition,
            top: `${forceToCenter ? 8 : topPosition}px`,
          }}
        />
        <span className="absolute top -left-0.5">
          <LinearPath
            pathData={linearPathData}
            color={color}
            height={linearHeight}
          />
        </span>
        <ChartFill
          pathData={fillPathData}
          color={color}
          width={fillWidth}
          height={fillHeight}
        />
        <div className="inline-flex items-center gap-1 absolute-x bottom-2">
          <GoldText text={donAwardAmount} />
          <DonLinear className="size-5" />
        </div>
      </div>
      <p className="text-center text-sm font-medium leading-130 mt-0.5">
        {invitationRange}
      </p>
    </div>
  );
};

const ReferralChartWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex justify-center gap-2 w-full mt-4 items-end">
      {children}
    </div>
  );
};

interface ReferralChartProps {
  invitedUsersCount: number;
  isPremium?: boolean;
  // Reward tiers from backend AppSettings — [0-100, 100-1000, 1000+].
  tierAmounts: number[];
}

const ReferralChart = ({
  invitedUsersCount,
  isPremium,
  tierAmounts,
}: ReferralChartProps) => {
  const betweenOfRange = (min: number, max: number) => {
    if (invitedUsersCount >= min && invitedUsersCount < max) {
      return true;
    }

    return false;
  };

  const getDonAwardAmount = (count: number) => {
    if (isPremium) {
      return count * 2;
    }
    return count;
  };

  return (
    <ReferralChartWrapper>
      {/* Chart 1 */}
      <ChartItem
        startTop={2}
        endTop={-7}
        fillHeight={71}
        isActive={invitedUsersCount > 0}
        showDot={betweenOfRange(0, 100)}
        min={0}
        max={100}
        invitedUsersCount={invitedUsersCount}
        donAwardAmount={getDonAwardAmount(tierAmounts[0])}
        linearPathData="M2 12.2009C34.1515 10.0857 66.1769 6.68042 98 2"
        linearHeight={15}
        fillPathData="M96 0C64.1769 4.68042 32.1515 8.08566 0 10.2009V57.4009C0 61.8813 0 64.1215 0.871948 65.8328C1.63893 67.3381 2.86278 68.562 4.36808 69.3289C6.07937 70.2009 8.31958 70.2009 12.8 70.2009H83.2C87.6804 70.2009 89.9206 70.2009 91.6319 69.3289C93.1372 68.562 94.3611 67.3381 95.1281 65.8328C96 64.1215 96 61.8813 96 57.4009V0Z"
        fillWidth={96}
        invitationRange="0-100"
      />

      {/* Chart 2 */}
      <ChartItem
        isActive={invitedUsersCount >= 100}
        startTop={12}
        endTop={-6}
        showDot={betweenOfRange(100, 1000)}
        min={100}
        max={1000}
        invitedUsersCount={invitedUsersCount}
        donAwardAmount={getDonAwardAmount(tierAmounts[1])}
        dotPosition="top-1"
        linearPathData="M98 2.7876C66.2679 10.3569 34.2414 16.6313 2 21.5951"
        linearHeight={24}
        fillPathData="M0 78.2001C0 82.6806 0 84.9208 0.871948 86.6321C1.63893 88.1374 2.86278 89.3612 4.36808 90.1282C6.07937 91.0001 8.31958 91.0001 12.8 91.0001H83.2C87.6804 91.0001 89.9206 91.0001 91.6319 90.1282C93.1372 89.3612 94.3611 88.1374 95.1281 86.6321C96 84.9208 96 82.6806 96 78.2001V0.787598C64.2679 8.35694 32.2414 14.6313 0 19.5951V78.2001Z"
        fillWidth={96}
        fillHeight={91}
        invitationRange="100-1000"
      />

      {/* Chart 3 */}
      <ChartItem
        forceToCenter
        startTop={20}
        endTop={-7}
        isActive={invitedUsersCount >= 1000}
        showDot={invitedUsersCount >= 1000}
        min={1000}
        max={Infinity}
        invitedUsersCount={invitedUsersCount}
        dotPosition="top-2"
        donAwardAmount={getDonAwardAmount(tierAmounts[2])}
        linearPathData="M98 2L90.7008 4.40104C61.4459 14.0244 31.8567 22.5117 2 29.8502"
        linearHeight={32}
        fillPathData="M0 107.2C0 111.68 0 113.921 0.871948 115.632C1.63893 117.137 2.86278 118.361 4.36808 119.128C6.07937 120 8.31958 120 12.8 120H83.2C87.6804 120 89.9206 120 91.6319 119.128C93.1372 118.361 94.3611 117.137 95.1281 115.632C96 113.921 96 111.68 96 107.2V0L88.7008 2.40104C59.4459 12.0244 29.8567 20.5117 0 27.8502V107.2Z"
        fillWidth={96}
        fillHeight={120}
        invitationRange="1000+"
      />
    </ReferralChartWrapper>
  );
};

export default ReferralChart;

const ChartItemSkeleton = ({ className }: { className: string }) => {
  return (
    <div>
      <div className={cn("w-[96px] skeleton rounded", className)} />
      <div className="w-[50px] mx-auto h-[18px] skeleton rounded mt-0.5" />
    </div>
  );
};

export const ReferralChartLoading = () => {
  return (
    <ReferralChartWrapper>
      <ChartItemSkeleton className="h-[71px]" />
      <ChartItemSkeleton className="h-[91px]" />
      <ChartItemSkeleton className="h-[120px]" />
    </ReferralChartWrapper>
  );
};
